import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router';

import { apiGet } from '../lib/api';

interface InvoiceRow {
  id: string;
  invoice_number: string;
  status: 'draft' | 'sent' | 'paid' | 'void' | 'overdue';
  total: number;
  currency: string;
  project_id: string;
  client_id: string;
  issue_date: string;
  due_date: string;
  paid_at: string | null;
  created_at: string;
}

interface WiseBalance {
  id: number;
  currency: string;
  amount: {
    value: number;
    currency: string;
  };
}

interface UsageSummary {
  totals: {
    raw_cost_usd: number;
    billable_amount_usd: number;
    requests_count: number;
    input_tokens: number;
    output_tokens: number;
  };
  by_provider: Array<{
    provider: string;
    total_billable: number;
    total_raw: number;
    total_requests: number;
  }>;
  anomalies: Array<{
    project_id: string;
    project_name: string;
    day: string;
    day_billable: number;
    baseline: number;
  }>;
}

interface ProjectRow {
  id: string;
  name: string;
  status: string;
  client_id: string;
}

interface ClientRow {
  id: string;
  display_name: string;
}

interface CurrencyTotal {
  currency: string;
  total: number;
}

export function OverviewPage() {
  const [invoices, setInvoices] = useState<InvoiceRow[]>([]);
  const [balances, setBalances] = useState<WiseBalance[]>([]);
  const [usageSummary, setUsageSummary] = useState<UsageSummary | null>(null);
  const [projects, setProjects] = useState<ProjectRow[]>([]);
  const [clients, setClients] = useState<ClientRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void (async () => {
      try {
        const [invoiceData, balanceData, usageData, projectData, clientData] = await Promise.all([
          apiGet<InvoiceRow[]>('/api/invoices'),
          apiGet<WiseBalance[]>('/api/wise/balances').catch(() => [] as WiseBalance[]),
          apiGet<UsageSummary>('/api/usage/summary').catch(() => null),
          apiGet<ProjectRow[]>('/api/projects'),
          apiGet<ClientRow[]>('/api/clients'),
        ]);
        setInvoices(invoiceData);
        setBalances(balanceData);
        setUsageSummary(usageData);
        setProjects(projectData);
        setClients(clientData);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load overview data');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const revenueByCurrency = useMemo(
    () => totalsByStatusAndCurrency(invoices, ['paid']),
    [invoices],
  );
  const outstandingByCurrency = useMemo(
    () => totalsByStatusAndCurrency(invoices, ['sent', 'overdue']),
    [invoices],
  );
  const draftByCurrency = useMemo(() => totalsByStatusAndCurrency(invoices, ['draft']), [invoices]);
  const overdueCount = useMemo(
    () => invoices.filter((i) => i.status === 'overdue').length,
    [invoices],
  );
  const activeProjects = useMemo(
    () => projects.filter((p) => p.status === 'active').length,
    [projects],
  );

  const recentInvoices = useMemo(() => invoices.slice(0, 8), [invoices]);

  const clientNameOf = (id: string): string => clients.find((c) => c.id === id)?.display_name ?? id;

  const paidUsd = useMemo(
    () => revenueByCurrency.find((row) => row.currency === 'USD')?.total ?? 0,
    [revenueByCurrency],
  );
  const hasNonUsdPaid = useMemo(
    () => revenueByCurrency.some((row) => row.currency !== 'USD' && row.total > 0),
    [revenueByCurrency],
  );

  const profitMargin = useMemo(() => {
    if (!usageSummary || paidUsd === 0 || hasNonUsdPaid) return null;
    const profit = paidUsd - usageSummary.totals.raw_cost_usd;
    return Math.round((profit / paidUsd) * 100);
  }, [paidUsd, hasNonUsdPaid, usageSummary]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold">Overview</h1>
          <p className="mt-1 text-sm text-slate-600">
            Revenue, receivables, usage, and financial snapshot.
          </p>
        </div>
      </div>

      {error ? <p className="mt-3 text-sm text-red-600">{error}</p> : null}
      {loading ? <p className="mt-3 text-sm text-slate-500">Loading dashboard...</p> : null}

      <div className="mt-4 grid gap-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
        <MetricCard
          title="Revenue (Paid)"
          value={formatCurrencyTotals(revenueByCurrency)}
          hint="Sum of paid invoices"
          accent="emerald"
        />
        <MetricCard
          title="Outstanding"
          value={formatCurrencyTotals(outstandingByCurrency)}
          hint="Sent + overdue"
          accent={outstandingByCurrency.some((row) => row.total > 0) ? 'amber' : 'slate'}
        />
        <MetricCard
          title="Draft"
          value={formatCurrencyTotals(draftByCurrency)}
          hint="Pending review"
          accent="purple"
        />
        <MetricCard
          title="Active Projects"
          value={String(activeProjects)}
          hint={`${projects.length} total`}
          accent="blue"
        />
        <MetricCard
          title="Clients"
          value={String(clients.length)}
          hint="Total clients"
          accent="slate"
        />
        <MetricCard
          title="Overdue"
          value={String(overdueCount)}
          hint={overdueCount > 0 ? 'Needs attention' : 'All clear'}
          accent={overdueCount > 0 ? 'rose' : 'emerald'}
        />
      </div>

      {countActiveCurrencies([...revenueByCurrency, ...outstandingByCurrency, ...draftByCurrency]) >
      1 ? (
        <p className="mt-2 text-xs text-slate-500">
          Revenue/outstanding/draft are shown per currency and not cross-currency summed.
        </p>
      ) : null}

      <div className="mt-4 grid gap-3 md:grid-cols-4">
        <MetricCard
          title="Usage Cost (30d)"
          value={formatMoney(usageSummary?.totals.raw_cost_usd ?? 0, 'USD')}
          hint="Raw provider cost"
          accent="slate"
        />
        <MetricCard
          title="Billable (30d)"
          value={formatMoney(usageSummary?.totals.billable_amount_usd ?? 0, 'USD')}
          hint="After markup"
          accent="slate"
        />
        <MetricCard
          title="API Requests (30d)"
          value={(usageSummary?.totals.requests_count ?? 0).toLocaleString()}
          hint="Total API calls"
          accent="slate"
        />
        <MetricCard
          title="Profit Margin"
          value={profitMargin !== null ? `${profitMargin}%` : '-'}
          hint={
            hasNonUsdPaid
              ? 'USD-only metric unavailable for mixed paid currencies'
              : 'Revenue vs raw cost'
          }
          accent={profitMargin !== null && profitMargin > 0 ? 'emerald' : 'slate'}
        />
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <section>
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold">Recent Invoices</h2>
            <Link to="/invoices" className="text-sm text-brand-700 hover:underline">
              View all
            </Link>
          </div>
          <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
                  <th className="px-3 py-2">Invoice</th>
                  <th className="px-3 py-2">Client</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2 text-right">Total</th>
                </tr>
              </thead>
              <tbody>
                {recentInvoices.map((inv) => (
                  <tr key={inv.id} className="border-b border-slate-100 last:border-0">
                    <td className="px-3 py-2">
                      <Link
                        to={`/invoices/${inv.id}`}
                        className="font-medium text-brand-700 hover:underline"
                      >
                        {inv.invoice_number}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-slate-600">{clientNameOf(inv.client_id)}</td>
                    <td className="px-3 py-2">
                      <StatusBadge status={inv.status} />
                    </td>
                    <td className="px-3 py-2 text-right font-medium">
                      {formatMoney(inv.total, inv.currency)}
                    </td>
                  </tr>
                ))}
                {recentInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                      No invoices yet
                    </td>
                  </tr>
                ) : null}
              </tbody>
            </table>
          </div>
        </section>

        <div className="space-y-6">
          <section>
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold">Wise Balances</h2>
              <Link to="/reconciliation" className="text-sm text-brand-700 hover:underline">
                Reconciliation
              </Link>
            </div>
            <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
              <table className="min-w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
                    <th className="px-3 py-2">Currency</th>
                    <th className="px-3 py-2 text-right">Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {balances.map((b) => (
                    <tr key={b.id} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2 font-medium">{b.currency}</td>
                      <td className="px-3 py-2 text-right">
                        {formatMoney(b.amount.value, b.amount.currency)}
                      </td>
                    </tr>
                  ))}
                  {balances.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="px-3 py-4 text-center text-slate-500">
                        No balances or Wise not configured
                      </td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </section>

          {usageSummary && usageSummary.by_provider.length > 0 ? (
            <section>
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold">Usage by Provider (30d)</h2>
                <Link to="/usage" className="text-sm text-brand-700 hover:underline">
                  View details
                </Link>
              </div>
              <div className="mt-2 overflow-x-auto rounded-lg border border-slate-200">
                <table className="min-w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-left text-slate-600">
                      <th className="px-3 py-2">Provider</th>
                      <th className="px-3 py-2 text-right">Billable</th>
                      <th className="px-3 py-2 text-right">Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {usageSummary.by_provider.map((row) => (
                      <tr key={row.provider} className="border-b border-slate-100 last:border-0">
                        <td className="px-3 py-2 font-medium capitalize">{row.provider}</td>
                        <td className="px-3 py-2 text-right">
                          {formatMoney(row.total_billable, 'USD')}
                        </td>
                        <td className="px-3 py-2 text-right">
                          {row.total_requests.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ) : null}
        </div>
      </div>

      {usageSummary && usageSummary.anomalies.length > 0 ? (
        <section className="mt-6">
          <h2 className="text-lg font-semibold">Cost Anomalies (30d)</h2>
          <p className="mt-1 text-sm text-slate-600">
            Days where usage exceeded 2x the rolling average.
          </p>
          <div className="mt-2 overflow-x-auto rounded-lg border border-amber-200 bg-amber-50">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-amber-200 text-left text-amber-800">
                  <th className="px-3 py-2">Project</th>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2 text-right">Daily Cost</th>
                  <th className="px-3 py-2 text-right">Baseline</th>
                </tr>
              </thead>
              <tbody>
                {usageSummary.anomalies.map((a, idx) => (
                  <tr
                    key={`${a.project_id}-${a.day}-${idx}`}
                    className="border-b border-amber-100 last:border-0"
                  >
                    <td className="px-3 py-2">
                      <Link
                        to={`/projects/${a.project_id}`}
                        className="font-medium text-amber-900 hover:underline"
                      >
                        {a.project_name}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-amber-800">{a.day}</td>
                    <td className="px-3 py-2 text-right font-medium text-amber-900">
                      {formatMoney(a.day_billable, 'USD')}
                    </td>
                    <td className="px-3 py-2 text-right text-amber-700">
                      {formatMoney(a.baseline, 'USD')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      ) : null}
    </div>
  );
}

function MetricCard(props: { title: string; value: string; hint: string; accent?: string }) {
  const accentMap: Record<string, string> = {
    emerald: 'border-emerald-200 bg-emerald-50',
    amber: 'border-amber-200 bg-amber-50',
    rose: 'border-rose-200 bg-rose-50',
    blue: 'border-blue-200 bg-blue-50',
    purple: 'border-purple-200 bg-purple-50',
    slate: 'border-slate-200 bg-slate-50',
  };
  const cardClass = accentMap[props.accent ?? 'slate'] ?? accentMap.slate;

  return (
    <div className={`rounded-xl border p-4 ${cardClass}`}>
      <p className="text-sm text-slate-600">{props.title}</p>
      <p className="mt-2 break-all text-xl font-semibold leading-snug text-slate-900">
        {props.value}
      </p>
      <p className="mt-1 text-xs text-slate-500">{props.hint}</p>
    </div>
  );
}

function StatusBadge(props: { status: string }) {
  const styleMap: Record<string, string> = {
    paid: 'bg-emerald-50 text-emerald-700',
    sent: 'bg-blue-50 text-blue-700',
    overdue: 'bg-amber-50 text-amber-700',
    void: 'bg-slate-100 text-slate-600',
    draft: 'bg-purple-50 text-purple-700',
  };
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-xs font-medium ${styleMap[props.status] ?? styleMap.draft}`}
    >
      {props.status}
    </span>
  );
}

function totalsByStatusAndCurrency(
  invoices: InvoiceRow[],
  statuses: InvoiceRow['status'][],
): CurrencyTotal[] {
  const byCurrency = new Map<string, number>();
  for (const invoice of invoices) {
    if (!statuses.includes(invoice.status)) {
      continue;
    }
    const currency = invoice.currency.toUpperCase();
    byCurrency.set(currency, (byCurrency.get(currency) ?? 0) + invoice.total);
  }

  return Array.from(byCurrency.entries())
    .map(([currency, total]) => ({ currency, total }))
    .sort((a, b) => a.currency.localeCompare(b.currency));
}

function formatCurrencyTotals(totals: CurrencyTotal[]): string {
  if (totals.length === 0) {
    return '$0.00';
  }

  if (totals.length === 1) {
    const first = totals[0];
    if (!first) {
      return '$0.00';
    }
    return formatMoney(first.total, first.currency);
  }

  return totals.map((row) => formatMoneyWithCode(row.total, row.currency)).join(' · ');
}

function formatMoneyWithCode(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    currencyDisplay: 'code',
    minimumFractionDigits: 2,
  }).format(value);
}

function countActiveCurrencies(totals: CurrencyTotal[]): number {
  return new Set(totals.filter((row) => row.total > 0).map((row) => row.currency)).size;
}

function formatMoney(value: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
    minimumFractionDigits: 2,
  }).format(value);
}
